import { useState, type MouseEvent } from "react";
import RadioButtonChecked from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import {
  Alert,
  Link,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { SessionContext } from "@thalamos/haven-common";
import {
  membershipLabel,
  sameSessionContext,
  sessionContextKey,
  sessionContextsFromMemberships,
} from "@thalamos/haven-common";

import api from "../api/index.js";
import { useUser } from "../Context/UserContext.js";
import type { SessionContextChoiceOutcome } from "../pages/SessionContextSelector/SessionContextSelectorPage.js";
import { createRoute } from "../routing/createRoute.js";

/** `failed` is no answer at all rather than a refusal; the recovery differs. */
export type SwitchOutcome = SessionContextChoiceOutcome | "failed";

/**
 * Changes the context the session acts in without the full-page picker. It
 * changes what the app lists, not merely how it looks.
 */
export const SessionContextSwitcher = () => {
  const { user, refetchUser } = useUser();
  const navigate = useNavigate();

  const choose = async (context: SessionContext): Promise<SwitchOutcome> => {
    const response = await api.user.setSessionContext(context);

    // 4xx resolves rather than throwing (`validateStatus` in `api/axios/api.ts`);
    // mistaken for success it re-skins the shell for a context nobody holds.
    if (response.status >= 400) {
      return "refused";
    }

    // The mutation has already committed, so a failed refresh is a client out
    // of step rather than a refusal — and navigating would carry it onward.
    try {
      await refetchUser();
    } catch {
      return "failed";
    }

    // Pages are scoped by context, so what is on screen may be nothing to do
    // with the one just moved to.
    navigate(createRoute.home(), { replace: true });
    return "accepted";
  };

  return (
    <SessionContextSwitcherContent
      current={user?.sessionContext ?? null}
      contexts={sessionContextsFromMemberships(user?.memberships ?? [])}
      onChoose={choose}
    />
  );
};

/**
 * The trigger and the picker panel. Everything is injected rather than reached
 * for, so a story can show any state without standing up a session.
 */
export const SessionContextSwitcherContent = ({
  current,
  contexts,
  onChoose,
}: {
  current: SessionContext | null;
  contexts: readonly SessionContext[];
  onChoose: (context: SessionContext) => Promise<SwitchOutcome>;
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Exclude<
    SwitchOutcome,
    "accepted"
  > | null>(null);

  if (current === null) {
    return null;
  }

  const currentName = t(`sessionContextKinds.${current.kind}`);
  // Every role the current context combines, so a refusal can be read against
  // what the session is acting as.
  const currentRoles = current.roles
    .map((role) => t(`roles.${role}`))
    .join(" · ");

  const currentContextLabel = (
    <Stack alignItems="center" spacing={0.25}>
      <Typography
        component="span"
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.4,
          textAlign: "center",
        }}
      >
        {currentName}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: 11,
          lineHeight: 1.4,
          textAlign: "center",
          opacity: 0.85,
        }}
      >
        {currentRoles}
      </Typography>
      {/* Drawn only where there is a name to draw: `membershipLabel`'s fallback
          is the organisation's id, which reads as noise at this size. */}
      {current.organisationName === undefined ? null : (
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            lineHeight: 1.4,
            textAlign: "center",
            opacity: 0.85,
          }}
        >
          {current.organisationName}
        </Typography>
      )}
    </Stack>
  );

  // Nothing to switch to, so no control that would do nothing.
  if (contexts.length <= 1) {
    return currentContextLabel;
  }

  // A refusal carried over would read as a refusal of whatever is chosen next.
  const close = () => {
    setAnchor(null);
    setProblem(null);
  };

  const choose = (context: SessionContext) => {
    // Not a change, and the request would be recorded as though it were.
    if (sameSessionContext(context, current)) {
      close();
      return;
    }

    setSubmitting(true);
    setProblem(null);

    void onChoose(context)
      .then((outcome) => {
        setProblem(outcome === "accepted" ? null : outcome);

        if (outcome === "accepted") {
          setAnchor(null);
        }
      })
      // A rejection is no answer rather than a refusal, so not reported as one.
      .catch(() => {
        setProblem("failed");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <>
      <Stack alignItems="center" spacing={0.5}>
        {currentContextLabel}
        <Link
          component="button"
          color="inherit"
          underline="always"
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
          aria-label={t("layout.switcher.open", {
            kindOfWork: currentName,
            organisation: membershipLabel(current),
          })}
          onClick={(event: MouseEvent<HTMLElement>) => {
            setAnchor(event.currentTarget);
          }}
          sx={{ fontSize: 12, fontWeight: 600 }}
        >
          {t("layout.switcher.change")}
        </Link>
      </Stack>

      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={close}
        // The trigger sits at the foot of the page, so a panel below it would
        // open off-screen.
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: 256,
              borderRadius: "10px",
            },
          },
        }}
      >
        <ListSubheader component="div" sx={{ lineHeight: 2.5 }}>
          {t("layout.switcher.heading")}
        </ListSubheader>

        {problem === null ? null : (
          <Alert severity="error" sx={{ mx: 1, my: 0.5 }}>
            {problem === "refused"
              ? t("errors.sessionContextFailed")
              : t("errors.sessionContextUnavailable")}
          </Alert>
        )}

        {contexts.map((context) => {
          const chosen = sameSessionContext(context, current);

          return (
            <MenuItem
              key={sessionContextKey(context)}
              role="menuitemradio"
              aria-checked={chosen}
              disabled={submitting}
              onClick={() => {
                choose(context);
              }}
            >
              {/* A mark, not a control: a real radio inside a menu item is a
                  second focusable thing saying what the item already says. */}
              <ListItemIcon>
                {chosen ? (
                  <RadioButtonChecked fontSize="small" color="primary" />
                ) : (
                  <RadioButtonUnchecked fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={t(`sessionContextKinds.${context.kind}`)}
                secondary={membershipLabel(context)}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
